import {
  isPassProductId,
  PassType,
} from "@/types/pass";
import { getEffectivePassType } from "@/lib/pass-utils";
import { formatKstCalendarDate } from "@/lib/kst-format";
import type { NicepayOrderStatus } from "@/lib/nicepay-payment";

/** 상품이 이용권(pass) 종류인지 확인 */
export function isPass(productId: string): boolean {
  return isPassProductId(productId);
}

/**
 * 상품이 코딩 요약 PDF인지 확인(JCG-2161).
 * 코딩 PDF productId는 "...-coding-pdf" 형태로 "coding"을 포함한다(src/constants/pdf-products.ts).
 */
export function isCodingPdf(productId: string): boolean {
  return !isPass(productId) && productId.includes("coding");
}

/** 상품이 이론 요약 PDF인지 확인(이용권·코딩 PDF가 아닌 나머지 PDF, JCG-2161) */
export function isTheoryPdf(productId: string): boolean {
  return !isPass(productId) && !isCodingPdf(productId);
}

/**
 * 유효기간 한국어 포맷 — null이면 "평생".
 *
 * 표기는 kst-format SSOT 를 쓴다. 직접 toLocaleDateString 하면 timeZone 이 안 박혀
 * `/membership`(SSOT 사용)과 `/my/pass` 가 같은 만료일을 다르게 표시한다 — 26-2 만료
 * (`2026-07-31T14:59:59Z`)가 KST 동쪽 사용자에게 8월 1일로 보인다.
 */
export function formatExpiryDate(expiresAt: string | null): string {
  if (!expiresAt) return "평생";
  return formatKstCalendarDate(expiresAt);
}

export interface PassInfoSummary {
  passType: PassType;
  expiresAt: string | null;
  purchasedAt: string | undefined;
  isActive: boolean;
  isTrial: boolean;
  isTrialExpired: boolean;
  freeTrialExpiresAt: string | null;
}

/** Clerk publicMetadata에서 이용권 정보 종합 추출 */
export function getPassInfoFromMetadata(
  publicMetadata: Record<string, unknown> | undefined
): PassInfoSummary {
  if (!publicMetadata) {
    return {
      passType: "free" as PassType,
      expiresAt: null,
      purchasedAt: undefined,
      isActive: true,
      isTrial: false,
      isTrialExpired: false,
      freeTrialExpiresAt: null,
    };
  }

  const passType = (publicMetadata.passType as PassType | undefined) || "free";
  const passExpiresAt =
    (publicMetadata.passExpiresAt as string | null | undefined) ?? null;
  const passPurchasedAt =
    (publicMetadata.passPurchasedAt as string | undefined) || undefined;
  const starterPurchasedAt =
    (publicMetadata.starterPurchasedAt as string | null) ?? null;
  const freeTrialStartedAt =
    (publicMetadata.freeTrialStartedAt as string | null) ?? null;
  const freeTrialExpiresAt =
    (publicMetadata.freeTrialExpiresAt as string | null) ?? null;
  const freeTrialTier =
    (publicMetadata.freeTrialTier as PassType | undefined) ?? undefined;

  const effective = getEffectivePassType({
    passType,
    passExpiresAt,
    starterPurchasedAt,
    freeTrialStartedAt,
    freeTrialExpiresAt,
    freeTrialTier,
  });

  return {
    passType: effective.passType,
    expiresAt: passExpiresAt,
    purchasedAt: passPurchasedAt,
    isActive: effective.isActive,
    isTrial: effective.isTrial,
    isTrialExpired: effective.isTrialExpired,
    freeTrialExpiresAt,
  };
}

/**
 * 이용권 요약 표시값(상태 라벨·무료 여부·유효기간)을 한 곳에서 파생한다.
 * 여러 위젯이 각자 ternary로 만들면 드리프트(예: 무료를 "활성화"로 표기)가 나므로 공유한다.
 * 소비처: DashboardPass, PassAside.
 */
export function getPassDisplay(passInfo: PassInfoSummary): {
  stateLabel: string;
  isFree: boolean;
  expiry: string;
} {
  const isFree = passInfo.passType === "free" && !passInfo.isTrial;
  const stateLabel = passInfo.isTrial
    ? "무료체험"
    : isFree
      ? "무료"
      : passInfo.isActive
        ? "활성화"
        : "만료";
  const expiry =
    passInfo.isTrial && passInfo.freeTrialExpiresAt
      ? formatExpiryDate(passInfo.freeTrialExpiresAt)
      : formatExpiryDate(passInfo.expiresAt);
  return { stateLabel, isFree, expiry };
}

/** publicMetadata에서 passExpiresAt만 단독 반환 */
export function getPassExpiryDateFromMetadata(
  publicMetadata: Record<string, unknown> | undefined
): string | null {
  if (!publicMetadata) return null;
  return (publicMetadata.passExpiresAt as string | null | undefined) ?? null;
}

export interface Purchase {
  isBundleComponent?: boolean;
  bundleOrderId?: string;
  id: string;
  orderId: string;
  goodsName: string;
  amount: number;
  status: string;
  paidAt: string;
  productId: string;
  downloadedAt?: string;
  downloadedPdfVersion?: string | null;
  downloadedPdfVersions?: string[] | null;
  receiptUrl?: string | null;
  paymentProvider?: "nicepay" | "toss";
  tid?: string | null;
  buyerEmail?: string | null;
  buyerName?: string | null;
}

export interface PendingPurchase {
  id: string;
  orderId: string;
  goodsName: string;
  amount: number;
  status:
    | Exclude<NicepayOrderStatus, "paid">
    | "payment_review_required";
  productId: string;
  tid?: string | null;
  payMethod?: string | null;
  vbankName?: string | null;
  vbankNumber?: string | null;
  vbankHolder?: string | null;
  vbankExpiresAt?: string | null;
  vbankIssuedAt?: string | null;
}

/** 구매 목록을 결제일 최신순(내림차순)으로 비파괴 정렬한다(공유 비교자, JCG-2161). */
export function sortPurchasesLatestFirst(purchases: Purchase[]): Purchase[] {
  return [...purchases].sort(
    (a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime()
  );
}

/** Nicepay 매출전표 링크. 무료 지급/과거 주문처럼 URL이 없으면 null. */
export function getPurchaseReceiptUrl(
  purchase: Pick<Purchase, "receiptUrl" | "tid">
): string | null {
  const receiptUrl = purchase.receiptUrl?.trim();
  if (!receiptUrl || purchase.tid === "FREE") {
    return null;
  }
  return receiptUrl;
}
